import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, ArrowLeftRight, Loader2, AlertTriangle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";

interface Proveedor {
  id: string;
  nombre: string;
  nit?: string | null;
  pais?: string | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  proveedorA: Proveedor;
  proveedorB: Proveedor;
  onMerged: () => void;
}

export function MergeProviderModal({ open, onOpenChange, proveedorA, proveedorB, onMerged }: Props) {
  const [source, setSource] = useState<Proveedor>(proveedorA);
  const [target, setTarget] = useState<Proveedor>(proveedorB);
  const [merging, setMerging] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { orgId } = useAuth();

  const handleSwap = () => {
    setSource(target);
    setTarget(source);
  };

  const handleMerge = async () => {
    setMerging(true);
    try {
      const { error } = await supabase.rpc("merge_proveedores", {
        source_id: source.id,
        target_id: target.id,
      });
      if (error) throw error;

      toast({ title: "✅ Proveedores unificados", description: `"${source.nombre}" fue absorbido por "${target.nombre}".` });
      queryClient.invalidateQueries({ queryKey: ["proveedores", orgId] });
      onMerged();
      onOpenChange(false);
    } catch (err: any) {
      toast({ title: "Error al unificar", description: err.message, variant: "destructive" });
    } finally {
      setMerging(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowLeftRight className="h-5 w-5 text-primary" />
            Unificar Proveedores
          </DialogTitle>
          <DialogDescription>
            Los embarques del proveedor de origen serán reasignados al destino. El proveedor de origen será eliminado.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-[1fr,auto,1fr] items-center gap-3 py-4">
          {/* Source (will be deleted) */}
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 space-y-2">
            <Badge variant="destructive" className="text-[10px]">Se eliminará</Badge>
            <p className="text-sm font-semibold text-foreground truncate">{source.nombre}</p>
            {source.nit && <p className="text-xs text-muted-foreground font-mono">{source.nit}</p>}
            {source.pais && <p className="text-xs text-muted-foreground">{source.pais}</p>}
          </div>

          {/* Swap button */}
          <Button variant="outline" size="icon" className="h-8 w-8 rounded-full" onClick={handleSwap} disabled={merging}>
            <ArrowLeftRight className="h-4 w-4" />
          </Button>

          {/* Target (will remain) */}
          <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 space-y-2">
            <Badge className="text-[10px] bg-primary text-primary-foreground">Permanecerá</Badge>
            <p className="text-sm font-semibold text-foreground truncate">{target.nombre}</p>
            {target.nit && <p className="text-xs text-muted-foreground font-mono">{target.nit}</p>}
            {target.pais && <p className="text-xs text-muted-foreground">{target.pais}</p>}
          </div>
        </div>

        <div className="flex items-start gap-2 p-3 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
          <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
          <p className="text-xs text-amber-800 dark:text-amber-300">
            Esta acción es <strong>irreversible</strong>. Todos los embarques de "{source.nombre}" se reasignarán a "{target.nombre}".
          </p>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={merging}>
            Cancelar
          </Button>
          <Button variant="destructive" onClick={handleMerge} disabled={merging}>
            {merging ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Unificando...
              </>
            ) : (
              <>
                <ArrowRight className="h-4 w-4 mr-2" />
                Confirmar Unificación
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
